import Vue, { ComponentOptions, VNode, VueConstructor } from 'vue';
import Component from 'vue-class-component';
import $ from 'jquery';
import { TWEEN, Tween, Easing } from './tween';
import { Box, Point, domRectToBox, stretchBox } from './svgutil';
import { CoordsMemento } from './coordsmemento';

/*
 * this is a view on a dot (graphviz) generated svg
 *
 * the svg that is fed into this view via the prop "svg"
 * is then rendered and clusters can be zoomed into view 
 * incl. animation
 */

export type RANKDIR = "TB" | "LR"

export interface NodeDescriptor {
    id: string,
    captions: string,
}


export interface GraphViewListener {

    /* these methods return true
       to indicate that the default behavior (zoom etc) 
       should be suppressed
       */

    onShowNode(node: SVGElement): boolean;
    onShowCluster(cluster: SVGElement): boolean;
}


/*
 * the actual GraphView-Vue component
 */
@Component({
    template: `
<div id="svgwrapper" class="column-container" :style="wrapperStyles">
    <div id="svgarea" class="column-container svgarea" style="cursor: pointer; user-select: none; flex: 1; height: 1px;">
    </div>
</div>
    `
})
export class GraphView extends Vue {

    listener: GraphViewListener

    refSize = 5000
    wrapperStyles = {
        "background-color": "white",
        "overflow": "hidden",
        "flex": "1",
    }
    svgarea: HTMLElement
    svg: SVGSVGElement
    scale = 1.0
    box: Box = { x: 0, y: 0, w: 100, h: 100 }
    animationStartBox: Box = { ...this.box }
    offset: Point
    animationDuration = 150
    inputSvg = ""
    coordsMemento : CoordsMemento
    mouseSetup = false

    created() {
    }

    mounted() {
    }

    rehashRawSvg(onComplete: () => void ) {
        let $area = $(this.$el).find("#svgarea");
        this.svgarea = $area.get(0)

        let edgeduration = 150;
        //CoordsMemento.setEdgeOpacity($area.find("svg"), edgeduration, 1, 0.75)
        setTimeout(() => {
            //console.log("setting input " + this.inputSvg)
            $area.html(this.inputSvg)
            let $svg = $area.find("svg")
            if ($svg.length == 0) {
                return
            }
            $svg.attr("width", "100%")
            $svg.attr("height", "100%")
            //$svg.attr("preserveAspectRatio", "xMidYMid meet")
            this.svg = $svg.get(0)
    
            $svg.find("title").remove()
            //$svg.find("polygon").remove()
            $svg.find("text").css("font-family", "Lato, sans-serif")
            $svg.find(".sl-1").find("ellipse").css("fill", "#ed7d31")
            $svg.find(".sl-1").find("text").css("fill", "#ffffff")
            $svg.find(".sl-2").find("ellipse").css("fill", "#000000")
            $svg.find(".sl-2").find("text").css("fill", "#ffffff")
            $svg.find(".sl-3").find("polygon").css("fill", "#fbe5d6")
    
            // make ellipses clickable
            $svg.find("ellipse").each((i, e) => {
                if (e.getAttribute("fill") == "none") {
                    e.setAttribute("fill", "white")
                }
            })
            
            let graphs = this.svg.getElementsByClassName("graph")
            let graph = graphs?.item(0) as SVGGElement
    
            // compute translation of the svg
            let matrix_ = graph.getCTM()
            let matrix = new DOMMatrix()
            matrix.a = matrix_.a
            matrix.b = matrix_.b
            matrix.c = matrix_.c
            matrix.d = matrix_.d
            matrix.e = matrix_.e
            matrix.f = matrix_.f
            let tmp = matrix.transformPoint({ x: 0, y: 0 })
            this.offset = { x: tmp.x, y: tmp.y }
    
            // kill transform
            if (graph.transform.baseVal) {
                while (graph.transform.baseVal.numberOfItems > 0) {
                    graph.transform.baseVal.removeItem(0)
                }
            }
            let animationDuration = 0
            let newCoordsMemento = CoordsMemento.valueOf($svg)
            //CoordsMemento.setEdgeOpacity($area.find("svg"), 0, 0, 0.75)
            if (this.coordsMemento) {
                /* we have captured the previous geometry
                 * lets move everything to the old positons and then
                 * animate to the new positions
                 */
                animationDuration = 250
                CoordsMemento.setTranslations($svg, animationDuration, this.coordsMemento, newCoordsMemento)
            }
            setTimeout(() => {
                //CoordsMemento.setEdgeOpacity($svg, edgeduration, 0.75, 1)
            }, 250)
         
            /* capture for new round */
            this.coordsMemento = CoordsMemento.valueOf($svg)
            this.setupMouse()
            onComplete()
            this.$emit("update")
        }, edgeduration)
    }

    showAll(animated?: boolean) {
        let $graph = $(this.svg).find(`#graph0`)
        let parent = $graph.get(0) as any as SVGGElement
        if (!parent) {
            console.error("No parent element found, bailing out")
            return
        }
        let box = domRectToBox(parent.getBBox())
        //stretchBox(box, 50)
        this.setViewBox(this.svg, box, animated ?? true)
    }

    showCluster(index: number) {
        let $cluster = $(this.svg).find(`#cluster-${index}`)
        if ($cluster.length == 0) {
            $cluster = $(this.svg).find(`#clust${index}`)
        }
        let cluster = $cluster.get(0) as any as SVGGElement
        if (!cluster) {
            console.error("cluster not found")
            return
        }
        if (this.listener && this.listener.onShowCluster(cluster)) {
            return
        }
        let box = domRectToBox(cluster.getBBox())
        stretchBox(box, 5)
        this.setViewBox(this.svg, box, true)
    }

    showNode(id: string) {
        let $node = $(this.svg).find(`#${id}`)
        let node = $node.get(0) as any as SVGGElement
        if (!node) {
            console.error("node not found")
            return
        }
        if (this.listener && this.listener.onShowNode(node)) {
            return
        }
        let box = domRectToBox(node.getBBox())
        stretchBox(box, 40)
        this.setViewBox(this.svg, box, true)
    }

    computeClusterIndices() {
        let indices: number[] = []
        let $cluster = $(this.svg).find(".cluster")
        $cluster.each((i, e) => {
            let id = e.getAttribute("id")
            let num = id.replace(/[^0-9]+/, "")
            indices.push(Number.parseInt(num))
        })
        return indices
    }

    computeNodeDescriptors() {
        let descriptors: NodeDescriptor[] = []
        descriptors.push({
            id: null,
            captions: ""
        })
        let $nodes = $(this.svg).find(".node")
        $nodes.each((i, e) => {
            let id = e.getAttribute("id")
            let $node = $(e)
            let $text = $node.find("text")
            let captions = $text.text()
            descriptors.push({
                id: id,
                captions: captions
            })
        })
        return descriptors
    }


    private setScale(scale: number) {
        //console.log("scale=" + scale)
        this.scale = scale;
    }

    private getNodeAt(pt: Point) : SVGGElement {
        let es = document.elementsFromPoint(pt.x, pt.y)
        if (es) {
            for (let e of es) {
                if (e instanceof SVGGraphicsElement) {
                    for (let curr : SVGElement = e; !!curr && curr.parentElement && curr.parentElement instanceof SVGElement; curr = curr.parentElement) {
                        if ($(curr).hasClass("node")) {
                            return curr as SVGGElement
                        }
                    }
                }
            }
        }
        return null
    }

    private getClusterAt(pt: Point) : SVGGElement {
        let es = document.elementsFromPoint(pt.x, pt.y)
        if (es) {
            for (let e of es) {
                if (e instanceof SVGGraphicsElement) {
                    for (let curr : SVGElement = e; !!curr && curr.parentElement && curr.parentElement instanceof SVGElement; curr = curr.parentElement) {
                        if ($(curr).hasClass("cluster")) {
                            return curr as SVGGElement
                        }
                    }
                }
            }
        }
        return null
    }

    private setViewBox(svg: Element, box: Box, animated?: boolean) {
        const svgSize = { w: this.svg.clientWidth, h: this.svg.clientHeight }
        this.setScale(svgSize.h / box.h);
        if (animated) {
            //TWEEN.removeAll()
            this.animationStartBox = { ...box }
            let start = {
                t: 0,
                x: this.box.x,
                y: this.box.y,
                w: this.box.w,
                h: this.box.h,
            }
            let end = {
                t: 0,
                x: box.x,
                y: box.y,
                w: box.w,
                h: box.h,
            }
            let duration = this.animationDuration
            let tween = new Tween(start, this.toString())
                .to(end, duration)
                .easing(Easing.Quadratic.InOut)
                .onUpdate((box: Box) => {
                    this.box = { ...box }
                    svg.setAttribute('viewBox', `${box.x} ${box.y} ${box.w} ${box.h}`);
                })
                .onComplete(() => {
                    this.box = { ...box }
                    svg.setAttribute('viewBox', `${this.box.x} ${this.box.y} ${this.box.w} ${this.box.h}`);
                    //TWEEN.remove(tween)
                });
            tween.start()
        } else {
            this.box = { ...box }
            this.animationStartBox = { ...box }
            svg.setAttribute('viewBox', `${this.box.x} ${this.box.y} ${this.box.w} ${this.box.h}`);
        }
    }


    private setupMouse() {
        if (this.mouseSetup) {
            return
        }
        this.mouseSetup = true
        const container = this.svgarea; //document.querySelector("#svgarea") as HTMLElement

        {
            let w = this.refSize
            let h = this.refSize
            this.setViewBox(this.svg, { x: 0, y: 0, w: w, h: h })
        }

        let buttonPressed = false
        let p0 = { x: 0, y: 0 }
        let p = { x: 0, y: 0 }
        let mouseDownBox: Box

        let endPanning = () => {
            if (buttonPressed) {
                buttonPressed = false
                let dx = (p0.x - p.x)
                let dy = (p0.y - p.y)
                if (Math.abs(dx) < 4 && Math.abs(dy) < 4) {
                    let node = this.getNodeAt({ ...p})
                    if (node) {
                        this.showNode(node.id)
                        return
                    }
                    let cluster = this.getClusterAt({...p})
                    if (cluster) {
                        let clusterIndex = Number.parseInt(cluster.id.replace("cluster-", ""))
                        this.showCluster(clusterIndex)
                        return
                    }

                    this.showAll()
                    return
                }
                dx /= this.scale
                dy /= this.scale
                let newBox = { x: mouseDownBox.x + dx, y: mouseDownBox.y + dy, w: mouseDownBox.w, h: mouseDownBox.h }
                this.setViewBox(this.svg, newBox)
            }
        }

        container.onwheel = (e) => {
            e.preventDefault()
            let w = this.box.w
            let h = this.box.h
            let mx = e.offsetX
            let my = e.offsetY
            let dw = w * Math.sign(-e.deltaY) * 0.05
            let dh = h * Math.sign(-e.deltaY) * 0.05
            let svgSize = { w: this.svg.clientWidth, h: this.svg.clientHeight }
            let dx = dw * mx / svgSize.w
            let dy = dh * my / svgSize.h
            let newBox = { x: this.box.x + dx, y: this.box.y + dy, w: this.box.w - dw, h: this.box.h - dh }
            this.setViewBox(this.svg, newBox)
        }

        container.onmousedown = (e) => {
            buttonPressed = true
            p0 = { x: e.x, y: e.y }
            mouseDownBox = { ... this.box }
        }

        container.onmousemove = (e) => {
            {
                let pt : Point = { x: e.x, y: e.y };
                let node = this.getNodeAt(pt)
                //console.info(node)
                if (node) {
                    let box = node.getBBox()
                    //console.log("node: " + node.id + " " + box.x + ", " + box.y + ", " + box.width + ", " + box.height)
                }
            }
            if (buttonPressed) {
                p = { x: e.x, y: e.y };
                let dx = (p0.x - p.x) / this.scale;
                let dy = (p0.y - p.y) / this.scale;
                let newBox = { x: mouseDownBox.x + dx, y: mouseDownBox.y + dy, w: mouseDownBox.w, h: mouseDownBox.h };
                this.setViewBox(this.svg, newBox)
            }
        }

        container.onmouseleave = (e) => {
            endPanning()
        }

        container.onmouseup = (e) => {
            p = { x: e.x, y: e.y }
            endPanning()
        }
    }

}
